/**
 * Purchases Page
 *
 * Mobile-first purchase list with cards on mobile, table on desktop.
 */

import { useState, useCallback, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Package, ChevronRight, Eye, CheckCircle2, XCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDate, debounce, formatAmountForExport } from '@/lib/utils'
import { HACIENDA_STATUS_LABELS, HACIENDA_STATUS_COLORS } from '@/types/bill'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'
import { DatePicker } from '@/components/ui/date-picker'
import { useToast } from '@/components/ui/toaster'
import { useTranslation } from '@/hooks/useTranslation'
import { useUrlPage, useUrlState } from '@/hooks/useUrlState'

interface Purchase {
  id: number
  provider_name: string | null
  invoice_number: string | null
  invoice_date: string | null
  subtotal: number
  total_sale_tax: number
  total: number
  is_paid: boolean
  is_cancelled: boolean
  hacienda_status?: string | null
  consecutivo_hacienda?: string | null
  clave_hacienda?: string | null
  created_at: string
}

const getHaciendaStatusIcon = (status?: string | null) => {
  switch (status) {
    case 'accepted':
    case 'success':
      return <CheckCircle2 className="h-3.5 w-3.5" />
    case 'rejected':
    case 'failed':
    case 'error':
      return <XCircle className="h-3.5 w-3.5" />
    case 'pending':
    case 'submitted':
      return <Clock className="h-3.5 w-3.5" />
    default:
      return <AlertCircle className="h-3.5 w-3.5" />
  }
}

const getHaciendaStatusBadge = (status?: string | null) => {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
        <AlertCircle className="h-3 w-3" />
        <span className="hidden sm:inline">{t('bills.haciendaNotSent')}</span>
      </span>
    )
  }
  const label = status === 'partial'
    ? t('purchases.haciendaPartial')
    : HACIENDA_STATUS_LABELS[status] || status
  const colors = HACIENDA_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-400'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${colors}`}>
      {getHaciendaStatusIcon(status)}
      {label}
    </span>
  )
}

export function PurchasesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useUrlPage()
  const [searchQuery, setSearchQuery] = useUrlState('q')
  const [searchInput, setSearchInput] = useState(searchQuery)
  const [haciendaFilter, setHaciendaFilter] = useUrlState('hacienda')
  const [dateFrom, setDateFrom] = useUrlState('desde')
  const [dateTo, setDateTo] = useUrlState('hasta')
  const hasDateFilter = !!(dateFrom || dateTo)

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
    }, 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  // Shared filter params (search + status + date range), reused by the list and
  // the export query.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams()
    if (searchQuery) params.set('search', searchQuery)
    if (haciendaFilter !== '') params.set('hacienda_status', haciendaFilter)
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', `${dateTo}T23:59:59`)
    return params
  }, [searchQuery, haciendaFilter, dateFrom, dateTo])

  // Fetch purchases (paginated, for display)
  // Igual que en facturas: pone al día los Mensajes Receptor que siguen sin
  // veredicto de Hacienda; antes solo cambiaban tocando Sincronizar en cada compra.
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: pendingSync, isFetching: syncingHacienda } = useQuery({
    queryKey: ['purchases-sync-pending'],
    queryFn: async () => {
      const response = await api.post('/purchases/sync-pending')
      return response.data as { checked: number; updated: number; rejected: number }
    },
    staleTime: 2 * 60 * 1000,
    retry: false,
  })
  useEffect(() => {
    if (!pendingSync?.updated) return
    queryClient.invalidateQueries({ queryKey: ['purchases'] })
    if (pendingSync.rejected > 0) {
      toast({
        title: t('invoicing.rejectedFound', { count: pendingSync.rejected }),
        variant: 'destructive',
      })
    }
  }, [pendingSync, queryClient, toast, t])

  const { data, isLoading } = useQuery({
    queryKey: ['purchases', page, searchQuery, haciendaFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params = buildFilterParams()
      params.set('page', page.toString())
      params.set('page_size', '20')
      const response = await api.get(`/purchases/?${params}`)
      return response.data
    },
  })

  // Export query: every matching row (not just the current page), only fetched
  // when a date filter is active (which is also when the export button shows).
  const { data: exportData } = useQuery({
    queryKey: ['purchases-export', searchQuery, haciendaFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params = buildFilterParams()
      params.set('all', 'true')
      const response = await api.get(`/purchases/?${params}`)
      return response.data
    },
    enabled: hasDateFilter,
  })

  return (
    <div className="space-y-4">
      {/* Header - Mobile optimized */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold flex items-center gap-2">
            {t('purchases.title')}
            {syncingHacienda && (
              <RefreshCw
                className="h-4 w-4 animate-spin text-muted-foreground"
                role="status"
                aria-label={t('invoicing.checkingHacienda')}
              />
            )}
          </h1>
          <p className="text-sm text-muted-foreground hidden md:block">
            {data?.total || 0} {t('purchases.title').toLowerCase()}
          </p>
        </div>
        <div className="flex gap-2">
          {hasDateFilter && (
            <ExportButtons
              data={exportData?.items || []}
              filename="compras"
              columns={[
                { key: 'id', header: 'ID', format: (v) => `#${v}` },
                { key: 'provider_name', header: t('purchases.provider'), format: (v) => v ? String(v) : '-' },
                { key: 'invoice_number', header: t('purchases.providerInvoice'), format: (v) => v ? String(v) : '-', asText: true },
                { key: 'invoice_date', header: t('purchases.purchaseDate'), format: (v) => v ? formatDate(v as string) : '-' },
                { key: 'created_at', header: t('purchases.registrationDate'), format: (v) => v ? formatDate(v as string) : '-' },
                { key: 'subtotal', header: t('purchases.subtotalNoTax'), format: (v) => formatAmountForExport(v as number), total: true },
                { key: 'total_sale_tax', header: t('purchases.iva'), format: (v) => formatAmountForExport(v as number), total: true },
                { key: 'total', header: t('reports.total'), format: (v) => formatAmountForExport(v as number), total: true },
                { key: 'hacienda_status', header: t('purchases.haciendaColumn'), format: (v) => v ? (HACIENDA_STATUS_LABELS[v as string] || String(v)) : t('bills.haciendaNotSent') },
                { key: 'consecutivo_hacienda', header: t('purchases.consecutivoHacienda'), format: (v) => v ? String(v) : '-', asText: true },
                { key: 'clave_hacienda', header: t('purchases.claveHacienda'), format: (v) => v ? String(v) : '-', asText: true },
              ]}
            />
          )}
          <Button onClick={() => navigate('/purchases/received')} className="hidden md:flex">
            <Plus className="h-4 w-4 mr-2" />
            {t('purchases.receivedDocuments')}
          </Button>
        </div>
      </div>

      {/* Search and Filters - Touch friendly */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder={t('purchases.searchProvider')}
            className="pl-10 h-12 text-base"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <select
          className="h-12 px-4 rounded-md border border-input bg-background text-base md:w-48"
          value={haciendaFilter}
          onChange={(e) => {
            setHaciendaFilter(e.target.value)
          }}
        >
          <option value="">{t('purchases.allHaciendaStatuses')}</option>
          <option value="submitted">{t('purchases.haciendaSubmitted')}</option>
          <option value="accepted">{t('purchases.haciendaAccepted')}</option>
          <option value="rejected">{t('purchases.haciendaRejected')}</option>
          <option value="partial">{t('purchases.haciendaPartial')}</option>
          <option value="error">{t('purchases.haciendaError')}</option>
        </select>
        <DatePicker
          value={dateFrom}
          onChange={(v) => {
            setDateFrom(v)
          }}
          max={dateTo || undefined}
          placeholder={t('purchases.dateFrom')}
          ariaLabel={t('purchases.dateFrom')}
          className="md:w-44"
        />
        <DatePicker
          value={dateTo}
          onChange={(v) => {
            setDateTo(v)
          }}
          min={dateFrom || undefined}
          placeholder={t('purchases.dateTo')}
          ariaLabel={t('purchases.dateTo')}
          className="md:w-44"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">{t('purchases.noPurchases')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery || haciendaFilter || hasDateFilter ? t('common.noResults') : t('purchases.addFirst')}
          </p>
          {!searchQuery && !haciendaFilter && !hasDateFilter && (
            <Button onClick={() => navigate('/purchases/received')}>
              <Plus className="h-4 w-4 mr-2" />
              {t('purchases.receivedDocuments')}
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Cards */}
          <div className="space-y-3 lg:hidden">
            {data?.items?.map((purchase: Purchase) => (
              <Link
                key={purchase.id}
                to={`/purchases/${purchase.id}`}
                className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
              <Card
                className="active:bg-muted/50 touch-manipulation cursor-pointer"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-medium">#{purchase.id}</p>
                        {getHaciendaStatusBadge(purchase.hacienda_status)}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {purchase.provider_name || '-'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {purchase.invoice_number || '-'} - {formatDate(purchase.invoice_date || purchase.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <p className="font-bold text-primary text-lg">
                        {formatCurrency(purchase.total)}
                      </p>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>

          {/* Desktop: Table */}
          <Card className="hidden lg:block">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium">ID</th>
                    <th className="text-left p-4 font-medium">{t('purchases.provider')}</th>
                    <th className="text-left p-4 font-medium">{t('purchases.providerInvoice')}</th>
                    <th className="text-left p-4 font-medium">{t('purchases.purchaseDate')}</th>
                    <th className="text-right p-4 font-medium">{t('reports.total')}</th>
                    <th className="text-center p-4 font-medium whitespace-nowrap">{t('purchases.haciendaColumn')}</th>
                    <th className="text-right p-4 font-medium">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.map((purchase: Purchase) => (
                    <tr
                      key={purchase.id}
                      className="border-b hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      onClick={() => navigate(`/purchases/${purchase.id}`)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') navigate(`/purchases/${purchase.id}`)
                      }}
                    >
                      <td className="p-4 font-medium">#{purchase.id}</td>
                      <td className="p-4">{purchase.provider_name || '-'}</td>
                      <td className="p-4">{purchase.invoice_number || '-'}</td>
                      <td className="p-4">{formatDate(purchase.invoice_date || purchase.created_at)}</td>
                      <td className="p-4 text-right font-medium text-primary">
                        {formatCurrency(purchase.total)}
                      </td>
                      <td className="p-4 text-center">
                        {getHaciendaStatusBadge(purchase.hacienda_status)}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/purchases/${purchase.id}`)
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {t('common.view')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Págination */}
          {data && data.pages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('common.page')} {data.page} {t('common.of')} {data.pages}
              </p>
              <Pagination
                currentPage={page}
                totalPages={data.pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      {/* Mobile FAB for New Purchase */}
      <Button
        onClick={() => navigate('/purchases/received')}
        className="md:hidden fixed bottom-20 right-4 h-14 w-14 rounded-full shadow-lg z-10"
        size="icon"
      >
        <Plus className="h-6 w-6" />
      </Button>
    </div>
  )
}
